import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useIsMobile } from "@/hooks/useIsMobile";
import { SOCIALS, LEADMAGNET_DESCRIPTION, COMMUNITY_PHOTO } from "@/data/site";
import { ListSignup } from "@/components/site/ListSignup";

// Dedicated text-list signup landing page — the About page's closing CTA
// routes here. Headline is drafted from her audience answer ("I resonate the
// most with the outcasts & neurodivergents") and her audience's season
// ("dark night of the soul on the road to enlightenment") — PLACEHOLDER,
// confirm wording with Jenn. Background: live performance shot from her
// About uploads (no dedicated community/crowd photo was supplied).
export default function Community() {
  const isMobile = useIsMobile();
  return (
    // flex column + min-h-screen: the standard "sticky footer" pattern —
    // <main> below gets flex-1, so it stretches to fill any leftover
    // viewport height itself, meaning <Footer/> always lands exactly at
    // the true bottom with nothing showing beneath it, regardless of how
    // short this page's own content is. Mobile: explicit black background
    // on the root, since the photo below is now a bounded block (not a
    // full-page fixed cover) — the area below it needs its own backdrop.
    <div className={`relative flex min-h-screen flex-col ${isMobile ? "bg-[var(--sm-black)]" : ""}`}>
      {/* Mobile: the crowd photo is a bounded block pinned to the top of
          the viewport (NOT a full-height fixed cover like desktop) — a
          reversal of an earlier pass that stretched/zoomed it to fill
          nearly the whole screen. Shrunk back down so the label/headline/
          card sit naturally within it, with the socials row and footer
          following in normal black-background flow below, reachable with
          a short, ordinary scroll (not zero, not excessive). Desktop is
          completely untouched (fixed inset-0, unchanged). */}
      <div
        className={isMobile ? "fixed inset-x-0 top-0 z-0 overflow-hidden" : "fixed inset-0 z-0 overflow-hidden"}
        style={isMobile ? { height: "58vh" } : undefined}
      >
        <img src={COMMUNITY_PHOTO} alt="" aria-hidden className="animate-kenburns h-full w-full object-cover" style={{ objectPosition: "center 35%" }} />
        <div aria-hidden className="haze-drift" />
        <div className="absolute inset-0 bg-black/65" />
      </div>
      <Navbar />
      {/* Header group (title + signup box + socials) shifted down ~7vh as
          one unit, per client request, once the footer-position bug above
          was fixed. Mobile: repositioned to sit centered within the
          shorter photo block above, instead of the desktop offset. */}
      <main
        className={`relative z-10 mx-auto w-full max-w-3xl flex-1 px-6 text-center ${isMobile ? "pb-16" : "pb-32"}`}
        style={{ paddingTop: isMobile ? "calc(4rem + 8vh)" : "calc(10rem + 7vh)" }}
      >
        <Reveal>
          <span className="overline">Community</span>
          {/* Mobile: must render on one line — a smaller guaranteed-fit size
              instead of the desktop text-5xl/6xl, which wraps at phone
              widths. */}
          <h1
            className={`font-display font-normal text-[var(--sm-text)] text-legible whitespace-nowrap ${
              isMobile ? "text-2xl" : "text-4xl sm:text-5xl"
            }`}
          >
            For the Outcasts &amp; the Awakening.
          </h1>
        </Reveal>

        {/* Wide, short signup box so more of the background photo shows.
            Routes to her list platform via ListSignup (an "opening soon"
            state until LAYLO_URL is set) rather than a native form.

            Rendered directly (no Reveal-driven transform/opacity animation)
            — backdrop-blur-sm on this box was getting visibly delayed by a
            couple of seconds, which traced back to browsers not compositing
            backdrop-filter correctly while an ancestor's `transform` is
            actively animating (Reveal's translateY fade-in). Present from
            the very first frame is more reliable than fighting that.

            The soft pulsing glow (animate-glow-pulse) is applied directly
            to this box, for the same reason — it must be animating from
            the very first frame with no delayed entrance, so it can't live
            behind a Reveal-gated fade-in either. */}
        <div
          className={`animate-glow-pulse mx-auto flex max-w-2xl flex-wrap items-center justify-center gap-6 rounded-xl border border-[var(--sm-gold)]/25 bg-black/45 px-8 py-4 backdrop-blur-sm ${
            isMobile ? "mt-6" : "mt-10"
          }`}
        >
          <p className="font-body max-w-sm text-left text-sm font-light text-white/70">
            Get texts from Jenn — {LEADMAGNET_DESCRIPTION.charAt(0).toLowerCase() + LEADMAGNET_DESCRIPTION.slice(1)}
          </p>
          <ListSignup compact label="Join My Inner Circle" />
        </div>

        <Reveal delay={0.3}>
          <div className={`flex flex-wrap items-center justify-center gap-x-7 gap-y-3 ${isMobile ? "mt-10" : "mt-14"}`}>
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-accent text-xs uppercase tracking-[0.2em] text-white/70 text-legible transition-colors hover:text-[var(--sm-gold-light)]"
              >
                {s.label}
              </a>
            ))}
          </div>
        </Reveal>
      </main>
      <Footer />
    </div>
  );
}
